// @ts-nocheck
/*
 * Port of views/dashboard/agent-realtime-stats/index.js (912 lines).
 * BYTE-IDENTICAL.
 *
 * Supervisor / team-lead real-time board:
 *  - SSE stream from `${BACKEND_API_BASE_URL}/v2/agents/team-lead-real-time`
 *    (token + x-client-source in query-string; auto-reconnect 3s on error).
 *  - Poll `API_TEAM_LEAD_AGENT_STATS` every 60s AND on visibilitychange
 *    (skips fetch while `document.hidden`).
 *  - Snoop actions (Barge / Whisper / Monitor) via POST
 *    `API_AGENT_SNOOP_START` and DELETE-with-body `API_AGENT_SNOOP_STOP`,
 *    only enabled while `dashboard.callStatus === "ANSWERED"` and
 *    `agentCallId` is set. Only one snoop session at a time.
 *  - Break timer counts DOWN from `breakDetail.duration` (minutes) since
 *    `user.updated_at`, showing `Expired +hh:mm:ss` past the deadline.
 *  - Theme switches on `document.body.classList` mutation (dark/light).
 *
 * V1 QUIRKS preserved:
 *  - `avatarColor` picks from a fixed 6-tuple by `agent.id % 6`
 *  - The Agent Detail modal renders `ReactApexChart` gauges. When the
 *    agent has no callStats row, the gauges section is skipped.
 *  - Queue-details grid `2Ã—2` order and formatting exactly as v1.
 *  - `agentsByStatus` sort order: Engaged, Available, On Break, Offline.
 */
import { queueWaitSeconds } from '@/utilities/queueWait';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Inline micro-shim for Modal only — preserves 2 Modal usages verbatim.
const Modal: any = ({
  show,
  onHide,
  size,
  centered: _c,
  dialogClassName,
  children,
  ...props
}: any) => (
  <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
    <DialogContent
      className={[
        size === 'lg' ? 'sm:max-w-2xl' : size === 'xl' ? 'sm:max-w-4xl' : '',
        dialogClassName || '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </DialogContent>
  </Dialog>
);
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
import ReactApexChart from 'react-apexcharts';
import {
  getApiCall,
  postApiCall,
  deleteApiCallWithBody,
} from '@/service/httpCall';
import {
  API_TEAM_LEAD_AGENT_STATS,
  API_AGENT_SNOOP_START,
  API_AGENT_SNOOP_STOP,
} from '@/service/url';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from '@/utilities/constant';
import { getUserAccessToken, getUser } from '@/utilities/setting';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useCallStore } from '@/router/zustand/callStore';
// v1 imports '../../../assets/custom/css/custom.css' here. The stylesheet is
// a 6070-line legacy sheet not carried over to v2. This page uses inline
// styles only, so the import is dropped â€” none of its class hooks are needed.

// â”€â”€ theme â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const DARK = {
  bg: '#0f1535',
  card: '#1a2150',
  cardAlt: '#141b42',
  border: 'rgba(255,255,255,0.08)',
  text: '#e2e8f0',
  muted: '#8892b0',
};
const LIGHT = {
  bg: '#f1f5f9',
  card: '#ffffff',
  cardAlt: '#f8fafc',
  border: 'rgba(0,0,0,0.08)',
  text: '#0f172a',
  muted: '#64748b',
};
const ACCENT = {
  green: '#16a34a',
  red: '#dc2626',
  blue: '#4361ee',
  cyan: '#0284c7',
  amber: '#d97706',
  orange: '#ea580c',
  purple: '#7c3aed',
};

const useTheme = () => {
  const [dark, setDark] = useState(
    typeof document !== 'undefined' &&
      document.body.classList.contains('dark'),
  );
  useEffect(() => {
    const body = document.body;
    const obs = new MutationObserver(() =>
      setDark(body.classList.contains('dark')),
    );
    obs.observe(body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return { ...(dark ? DARK : LIGHT), ...ACCENT, isDark: dark };
};

// â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const fmt = (label: any, value: any, color: any, T: any) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '6px 0',
      borderBottom: `1px solid ${T.border}`,
    }}
  >
    <span style={{ fontSize: 13, color: T.muted }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 700, color: color || T.text }}>
      {value}
    </span>
  </div>
);

const statusColor = (agent: any, user: any, T: any) => {
  if (Number(agent?.call_status) === 1) return { color: T.amber, label: 'Engaged' };
  if (user?.break_id) return { color: T.orange, label: 'On Break' };
  if (agent?.login_status === '1') return { color: T.green, label: 'Available' };
  return { color: T.red, label: 'Offline' };
};

const StatCard = ({ title, value, sub, color, T, children }: any) => (
  <div
    style={{
      borderRadius: 16,
      padding: '20px 22px',
      border: `1px solid ${T.border}`,
      height: '100%',
      boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(15,23,42,0.07)',
    }}
  >
    <div
      style={{
        fontSize: 12,
        color: T.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 10,
      }}
    >
      {title}
    </div>
    {value !== undefined && (
      <div
        style={{
          fontSize: 40,
          fontWeight: 800,
          color: color || T.text,
          lineHeight: 1,
          marginBottom: 4,
        }}
      >
        {value}
      </div>
    )}
    {sub && (
      <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{sub}</div>
    )}
    {children}
  </div>
);

// â”€â”€ profile badges (read-only status indicators) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PROFILE_META: any = {
  Mobile: 'MOB',
  'Soft Phone': 'SIP',
  WEBRTC: 'WEB',
};

const ProfileBadges = ({ profiles }: any) => {
  const T = useTheme();

  return (
    <div
      style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap' }}
    >
      {Object.entries(PROFILE_META).map(([type, short]: any) => {
        const profile = (profiles || []).find((p: any) => p.type === type);
        const isActive = !!profile?.active_profile;
        const color = isActive ? T.green : T.red;
        return (
          <span
            key={type}
            title={isActive ? `Logged in on ${type}` : `Not logged in on ${type}`}
            style={{
              padding: '1px 7px',
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 700,
              backgroundColor: `${color}20`,
              color,
              border: `1px solid ${color}55`,
              cursor: 'default',
              userSelect: 'none',
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
                marginRight: 3,
                verticalAlign: 'middle',
              }}
            />
            {short}
          </span>
        );
      })}
    </div>
  );
};

// â”€â”€ break timer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const BreakTimer = ({ user, currentTime }: any) => {
  const T = useTheme();
  if (!user?.break_id || !user?.breakDetail) return null;
  const durationSecs = Number(user.breakDetail.duration || 0) * 60;
  const breakStartedAt = user.updated_at
    ? Math.floor(new Date(user.updated_at).getTime() / 1000)
    : null;
  if (!breakStartedAt) return null;
  const remaining = durationSecs - (currentTime - breakStartedAt);
  const abs = Math.abs(Math.floor(remaining));
  const h = Math.floor(abs / 3600),
    m = Math.floor((abs % 3600) / 60),
    s = abs % 60;
  const hms = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    s,
  ).padStart(2, '0')}`;
  return (
    <span
      style={{
        fontSize: 10,
        color: remaining <= 0 ? T.red : T.amber,
        fontWeight: 600,
        display: 'block',
        marginTop: 2,
      }}
    >
      {user.breakDetail.title} - {remaining <= 0 ? `Expired +${hms}` : hms}
    </span>
  );
};

// â”€â”€ main dashboard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const AgentRealtimeStats = () => {
  const T = useTheme();
  const [groups, setGroups] = useState<any[]>([]);
  const [queue, setQueue] = useState<any>(null);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null); // { agent, user, dashboard }
  const [callStats, setCallStats] = useState<any[]>([]);
  const [currentTime, setCurrentTime] = useState(() =>
    Math.floor(Date.now() / 1000),
  );
  const [loading, setLoading] = useState(true);
  const [snoopSession, setSnoopSession] = useState<any>(null);
  const [snoopLoading, setSnoopLoading] = useState(false);

  useEffect(() => {
    let t: any = null;
    const start = () => {
      if (t) return;
      t = setInterval(() => setCurrentTime(Math.floor(Date.now() / 1000)), 1000);
    };
    const stop = () => {
      if (t) {
        clearInterval(t);
        t = null;
      }
    };
    const onVisible = () => {
      if (document.hidden) {
        stop();
      } else {
        setCurrentTime(Math.floor(Date.now() / 1000));
        start();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    if (!document.hidden) start();
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      stop();
    };
  }, []);

  useEffect(() => {
    const token = getUserAccessToken();
    let es: any = null;
    let retryTimer: any = null;
    let closed = false;
    let first = true;

    const connect = () => {
      es = new EventSource(
        `${BACKEND_API_BASE_URL}/v2/agents/team-lead-real-time?token=${token}&x-client-source=${CLIENT_SOURCE}`,
      );
      es.onmessage = (e: any) => {
        try {
          const payload = JSON.parse(e.data)?.data || {};
          setGroups(payload.groups || []);
          if (payload.queue) setQueue(payload.queue);
        } catch (_) {}
        if (first) {
          setLoading(false);
          first = false;
        }
      };
      es.onerror = () => {
        es.close();
        setLoading(false);
        if (!closed) {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retryTimer);
      if (es) es.close();
    };
  }, []);

  // Call stats (REST, every 60s)
  const fetchStats = useCallback(() => {
    getApiCall(API_TEAM_LEAD_AGENT_STATS)
      .then((d: any) => setCallStats(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let t: any;
    const poll = () => {
      if (!document.hidden) fetchStats();
      t = setTimeout(poll, 60000);
    };
    const onVisible = () => {
      if (!document.hidden) fetchStats();
    };
    document.addEventListener('visibilitychange', onVisible);
    poll();
    return () => {
      if (t) clearTimeout(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchStats]);

  const getTeamLead = useCallback(() => {
    const me = getUser();
    const myAgent = me?.agentData || {};
    const profiles = myAgent?.agentTelephony?.profile || [];
    const active = profiles.find((p: any) => p.active_profile === true);
    return {
      teamLeadAgentId: myAgent?.id,
      listenerExt: active?.number?.number,
    };
  }, []);

  const SNOOP_MODE: any = { 'Barge In': 'barge', Whisper: 'whisper', Monitor: 'monitor' };

  const startSnoop = useCallback(
    async (label: string, item: any) => {
      const agent = item?.agent || {};
      const dash = item?.dashboard || {};
      if (String(dash?.callStatus || '').toUpperCase() !== 'ANSWERED') return;

      const mode = SNOOP_MODE[label];
      const { teamLeadAgentId, listenerExt } = getTeamLead();
      const agentCallId = dash?.agentCallId;
      const asteriskId = dash?.asterisk_id || '1';

      if (!teamLeadAgentId) {
        toast.error('Team lead agent not found');
        return;
      }
      if (!listenerExt) {
        toast.error('No active profile/extension. Login to a profile first.');
        return;
      }
      if (!agentCallId) {
        toast.error('No active call id for this agent');
        return;
      }

      setSnoopLoading(true);
      try {
        const url = `${API_AGENT_SNOOP_START.replace(
          ':id',
          encodeURIComponent(agentCallId),
        )}?asterisk_id=${encodeURIComponent(asteriskId)}`;
        const data: any = await postApiCall(url, {
          to: String(listenerExt),
          mode,
          from: String(listenerExt),
          snoop_agent_id: agent.id,
          team_lead_agent_id: teamLeadAgentId,
          // pbx_name: useCallStore.getState().pbxName || null,
        });
        if (data?.pbx_name) {
          useCallStore.getState().setSipData({ pbxName: String(data.pbx_name) });
        }
        setSnoopSession({
          agentId: agent.id,
          mode,
          conferenceId: data?.listenerAgentConferenceId || null,
        });
        toast.success(`${label} started - ${agent.agent_name || 'agent'}`);
      } catch (err: any) {
        toast.error(friendlyApiError(err) || `${label} failed`);
      } finally {
        setSnoopLoading(false);
      }
    },
    [getTeamLead],
  );

  const stopSnoop = useCallback(async () => {
    if (!snoopSession) return;
    const { teamLeadAgentId, listenerExt } = getTeamLead();
    setSnoopLoading(true);
    try {
      if (snoopSession.conferenceId) {
        const url = `${API_AGENT_SNOOP_STOP.replace(
          ':id',
          encodeURIComponent(snoopSession.conferenceId),
        )}?asterisk_id=1`;
        await deleteApiCallWithBody(url, {
          from: listenerExt ? String(listenerExt) : null,
          snoop_agent_id: snoopSession.agentId,
          team_lead_agent_id: teamLeadAgentId,
          pbx_name: useCallStore.getState().pbxName || null,
        });
      }
      toast.success('Snoop stopped');
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Stop snoop failed');
    } finally {
      setSnoopSession(null);
      setSnoopLoading(false);
    }
  }, [snoopSession, getTeamLead]);

  // â”€â”€ derived data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const allAgents = useMemo(
    () => groups.flatMap((g: any) => g.agents),
    [groups],
  );

  const counts = useMemo(
    () => ({
      total: allAgents.length,
      online: allAgents.filter((a: any) => a?.agent?.login_status === '1').length,
      offline: allAgents.filter((a: any) => a?.agent?.login_status !== '1').length,
      onCall: allAgents.filter((a: any) => Number(a?.agent?.call_status) === 1)
        .length,
      onBreak: allAgents.filter((a: any) => a?.user?.break_id).length,
      available: allAgents.filter(
        (a: any) =>
          a?.agent?.login_status === '1' &&
          !Number(a?.agent?.call_status) &&
          !a?.user?.break_id,
      ).length,
    }),
    [allAgents],
  );

  const totalStats = useMemo(
    () => ({
      inbound: callStats.reduce((s: number, a: any) => s + (a.inbound || 0), 0),
      outbound: callStats.reduce((s: number, a: any) => s + (a.outbound || 0), 0),
      total: callStats.reduce((s: number, a: any) => s + (a.total_calls || 0), 0),
      connected: callStats.reduce(
        (s: number, a: any) => s + (a.connected || 0),
        0,
      ),
      failed: callStats.reduce((s: number, a: any) => s + (a.failed || 0), 0),
      abandoned: callStats.reduce((s: number, a: any) => s + (a.abandoned || 0), 0),
    }),
    [callStats],
  );

  const topAgents = useMemo(
    () =>
      [...callStats]
        .sort((a: any, b: any) => (b.total_calls || 0) - (a.total_calls || 0))
        .slice(0, 7),
    [callStats],
  );

  const barData = useMemo(
    () =>
      callStats.map((a: any) => ({
        name: a.agent_name?.split(' ')[0] || '-',
        Inbound: a.inbound || 0,
        Outbound: a.outbound || 0,
      })),
    [callStats],
  );

  const agentsByStatus = useMemo(() => {
    const list = allAgents.map((item: any) => {
      const { color, label } = statusColor(item?.agent, item?.user, T);
      return { ...item, statusLabel: label, statusColor: color };
    });
    const order: any = { Engaged: 0, Available: 1, 'On Break': 2, Offline: 3 };
    return [...list].sort(
      (a: any, b: any) => (order[a.statusLabel] || 9) - (order[b.statusLabel] || 9),
    );
  }, [allAgents]);

  if (loading)
    return (
      <div
        style={{
          background: T.bg,
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 16,
        }}
      >
        <div style={{ color: T.muted, fontSize: 14 }}>
          Loading real-time data...
        </div>
      </div>
    );

  const gridCard: any = {
    borderRadius: 16,
    padding: '20px 22px',
    border: `1px solid ${T.border}`,
    boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(15,23,42,0.07)',
  };
  const sectionTitle = (txt: string) => (
    <div
      style={{
        fontSize: 12,
        color: T.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 14,
      }}
    >
      {txt}
    </div>
  );

  return (
    <FillPage>
      <PageHeader
        title="Agent Realtime Stats"
        description="Live supervisor board — SSE feed with Barge / Whisper / Monitor snoop controls."
      />
      <div
        style={{
          borderRadius: 16,
          transition: 'background 0.3s',
        }}
      >
      {/* â”€â”€ Row 1: top stat cards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))',
          gap: 14,
          marginBottom: 20,
        }}
      >
        <StatCard
          title="Total Agents"
          value={counts.total}
          color={T.blue}
          sub={`${counts.online} online`}
          T={T}
        />
        <StatCard
          title="Available"
          value={counts.available}
          color={T.green}
          sub="Ready for calls"
          T={T}
        />
        <StatCard
          title="Engaged / On Call"
          value={counts.onCall}
          color={T.amber}
          sub="Active calls"
          T={T}
        />
        <StatCard
          title="On Break"
          value={counts.onBreak}
          color={T.orange}
          sub="On break"
          T={T}
        />
        <StatCard
          title="Offline"
          value={counts.offline}
          color={T.red}
          sub="Not logged in"
          T={T}
        />
        <StatCard
          title="Inbound Today"
          value={totalStats.inbound}
          color={T.cyan}
          sub={`${totalStats.outbound} outbound`}
          T={T}
        />
        <StatCard
          title="Total Calls"
          value={totalStats.total}
          color={T.purple}
          sub={`${totalStats.connected} answered`}
          T={T}
        />
      </div>

      {/* â”€â”€ Row 2: Call metrics + Queue â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div
        className="grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1.4fr]"
        style={{
          display: 'grid',
          gap: 14,
          marginBottom: 20,
        }}
      >
        {/* Call Metrics */}
        <div style={gridCard}>
          {sectionTitle('Call Metrics')}
          {fmt('Inbound Calls', totalStats.inbound, T.cyan, T)}
          {fmt('Outbound Calls', totalStats.outbound, T.blue, T)}
          {fmt('Answered', totalStats.connected, T.green, T)}
          {fmt('Failed', totalStats.failed, T.red, T)}
          {fmt('Abandoned', totalStats.abandoned, T.amber, T)}
          {fmt('Call Groups', groups.length, T.purple, T)}
        </div>

        {/* Queue card */}
        <div style={{ ...gridCard, position: 'relative', overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: `${T.purple}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 16 }}>â˜°</span>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                Call Group
              </span>
            </div>
            <button
              onClick={() => setShowQueueModal(true)}
              style={{
                background: `${T.purple}18`,
                color: T.purple,
                border: `1px solid ${T.purple}55`,
                borderRadius: 20,
                padding: '3px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              Details <span style={{ fontSize: 10 }}>›</span>
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {[
              { label: 'WAITING', value: queue?.count ?? 0, color: T.blue, big: true },
              { label: 'AVERAGE', value: queue?.average ?? '-', color: T.text, big: true },
              { label: 'MAXIMUM', value: queue?.maximum ?? '00:00:00', color: T.amber, big: false },
              { label: 'MAX DELAY', value: queue?.maxDelay ?? '00:00:00', color: T.orange, big: false },
            ].map(({ label, value, color, big }: any) => (
              <div
                key={label}
                style={{
                  background: T.isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                  borderRadius: 10,
                  padding: '10px 12px',
                  border: `1px solid ${T.border}`,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: T.muted,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    marginBottom: 4,
                  }}
                >
                  {label}
                </div>
                <div
                  style={{
                    fontSize: big ? 22 : 15,
                    fontWeight: 800,
                    color,
                    fontVariantNumeric: 'tabular-nums',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {!big && <span style={{ fontSize: 12 }}>â±</span>}
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Agents list */}
        <div style={{ ...gridCard, overflowY: 'auto', maxHeight: 240 }}>
          {sectionTitle('Agents')}
          {agentsByStatus.length === 0 ? (
            <div style={{ color: T.muted, fontSize: 13 }}>No agents</div>
          ) : (
            agentsByStatus.map((item: any, i: number) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '5px 0',
                  borderBottom: `1px solid ${T.border}`,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 13, color: T.text, fontWeight: 600 }}
                  >
                    {item?.agent?.agent_name}
                  </div>
                  <BreakTimer user={item?.user} currentTime={currentTime} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* â”€â”€ Row 3: Bar chart + Top agents â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div
        className="grid-cols-1 lg:grid-cols-[1.8fr_1fr]"
        style={{
          display: 'grid',
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div style={gridCard}>
          {sectionTitle('Calls Today per Agent')}
          {barData.length === 0 ? (
            <div style={{ color: T.muted, fontSize: 13 }}>No call data yet</div>
          ) : (
            <div style={{ overflowX: barData.length > 8 ? 'auto' : 'visible' }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={barData}
                  barSize={18}
                  margin={{ top: 4, right: 8, left: -10, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.06)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: T.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: T.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: T.cardAlt,
                      border: `1px solid ${T.border}`,
                      borderRadius: 8,
                      color: T.text,
                      fontSize: 12,
                    }}
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  />
                  <Bar dataKey="Inbound" fill={T.cyan} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Outbound" fill={T.blue} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  marginTop: 8,
                  justifyContent: 'center',
                }}
              >
                {[
                  { c: T.cyan, l: 'Inbound' },
                  { c: T.blue, l: 'Outbound' },
                ].map(({ c, l }: any) => (
                  <span
                    key={l}
                    style={{
                      fontSize: 11,
                      color: T.muted,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        background: c,
                        display: 'inline-block',
                      }}
                    />
                    {l}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Most calls resolved */}
        <div style={gridCard}>
          {sectionTitle('Most Calls Resolved')}
          {topAgents.length === 0 ? (
            <div style={{ color: T.muted, fontSize: 13 }}>No data yet</div>
          ) : (
            topAgents.map((a: any, i: number) => (
              <div
                key={a.agent_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderBottom: `1px solid ${T.border}`,
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: i === 0 ? T.amber : T.card,
                      border: `1px solid ${T.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                      color: i === 0 ? T.bg : T.muted,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 13, color: T.text }}>
                    {a.agent_name}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: i === 0 ? T.amber : T.text,
                  }}
                >
                  {a.total_calls}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* â”€â”€ Row 4: Per-group agent tables â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {groups.map((group: any) => (
        <div
          key={group.call_group_id}
          style={{ ...gridCard, marginBottom: 14 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: T.text,
                borderLeft: `4px solid ${T.blue}`,
                paddingLeft: 10,
              }}
            >
              {group.call_group_name}
            </div>
            <span style={{ fontSize: 11, color: T.muted }}>
              {group.agents.length} agent{group.agents.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <thead>
                <tr
                  style={{
                    color: T.muted,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {['#', 'Agent Name', 'Call Status', 'Activity', 'Extension', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          padding: '6px 10px',
                          textAlign: 'left',
                          borderBottom: `1px solid ${T.border}`,
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {group.agents.map((item: any, idx: number) => {
                  const agent = item?.agent || {};
                  const user = item?.user || {};
                  const dash = item?.dashboard || {};
                  const profiles = agent?.agentTelephony?.profile || [];
                  const active = profiles.find(
                    (p: any) => p.active_profile === true,
                  );
                  const activity = dash?.status
                    ? String(dash.status).charAt(0).toUpperCase() +
                      String(dash.status).slice(1).toLowerCase().replace(/_/g, ' ')
                    : '-';
                  const actColor = (() => {
                    const s = String(dash?.status || '').toLowerCase();
                    if (s.includes('call')) return T.amber;
                    if (s.includes('break')) return T.orange;
                    if (s === 'ready' || s === 'idle') return T.green;
                    return T.muted;
                  })();
                  return (
                    <tr
                      key={agent.id || idx}
                      style={{ borderBottom: `1px solid ${T.border}` }}
                    >
                      <td style={{ padding: '8px 10px', color: T.muted }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div
                          onClick={() =>
                            setSelectedAgent({ agent, user, dashboard: dash })
                          }
                          style={{
                            fontWeight: 600,
                            color: T.blue,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor:
                                agent.login_status === '1' ? T.green : T.red,
                              boxShadow:
                                agent.login_status === '1'
                                  ? `0 0 0 2px ${T.green}30`
                                  : 'none',
                            }}
                          />
                          {agent.agent_name || '-'}
                        </div>
                        <ProfileBadges profiles={profiles} />
                        <BreakTimer user={user} currentTime={currentTime} />
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <span
                          style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            background:
                              Number(agent.call_status) === 1
                                ? `${T.amber}20`
                                : `${T.muted}20`,
                            color:
                              Number(agent.call_status) === 1 ? T.amber : T.muted,
                            border: `1px solid ${
                              Number(agent.call_status) === 1 ? T.amber : T.muted
                            }44`,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background:
                                Number(agent.call_status) === 1
                                  ? T.amber
                                  : T.muted,
                              display: 'inline-block',
                              marginRight: 5,
                              verticalAlign: 'middle',
                            }}
                          />
                          {Number(agent.call_status) === 1 ? 'In Call' : 'Idle'}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          color: actColor,
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        {activity}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          color: T.muted,
                          fontSize: 12,
                        }}
                      >
                        {active?.number?.number || '-'}
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        {(() => {
                          const onCall =
                            String(dash?.callStatus || '').toUpperCase() ===
                            'ANSWERED';
                          const hasCallId = !!dash?.agentCallId;
                          const btns = [
                            { key: 'Barge In', mode: 'barge', color: T.red, icon: 'ðŸ“¢' },
                            { key: 'Whisper', mode: 'whisper', color: T.purple, icon: 'ðŸ—£' },
                            { key: 'Monitor', mode: 'monitor', color: T.cyan, icon: 'ðŸŽ§' },
                          ];
                          const snoopActive = !!snoopSession;
                          return (
                            <div style={{ display: 'flex', gap: 6 }}>
                              {btns.map((b: any) => {
                                const isActive =
                                  snoopActive &&
                                  snoopSession.agentId === agent.id &&
                                  snoopSession.mode === b.mode;
                                const enabled =
                                  onCall &&
                                  hasCallId &&
                                  !snoopLoading &&
                                  (!snoopActive || isActive);
                                const title = isActive
                                  ? `Stop ${b.key}`
                                  : !onCall
                                  ? `${b.key} (agent not on call)`
                                  : !hasCallId
                                  ? 'Call connecting - please wait'
                                  : snoopActive
                                  ? 'Another snoop in progress'
                                  : b.key;
                                return (
                                  <button
                                    key={b.key}
                                    type="button"
                                    disabled={!enabled}
                                    title={title}
                                    onClick={() =>
                                      isActive ? stopSnoop() : startSnoop(b.key, item)
                                    }
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 4,
                                      padding: '3px 9px',
                                      borderRadius: 8,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      whiteSpace: 'nowrap',
                                      background: isActive
                                        ? b.color
                                        : enabled
                                        ? `${b.color}20`
                                        : `${T.muted}15`,
                                      color: isActive
                                        ? '#fff'
                                        : enabled
                                        ? b.color
                                        : T.muted,
                                      border: `1px solid ${
                                        isActive
                                          ? b.color
                                          : enabled
                                          ? b.color
                                          : T.muted
                                      }44`,
                                      cursor: enabled ? 'pointer' : 'not-allowed',
                                      opacity: enabled || isActive ? 1 : 0.55,
                                    }}
                                  >
                                    <span>{b.icon}</span>
                                    {isActive ? `Stop ${b.key}` : b.key}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* â”€â”€ Row 5: Agent call stats table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {callStats.length > 0 && (
        <div style={{ ...gridCard, marginBottom: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
              Today's Agent Call Statistics
            </div>
            <button
              onClick={fetchStats}
              style={{
                background: `${T.blue}20`,
                color: T.blue,
                border: `1px solid ${T.blue}44`,
                borderRadius: 8,
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              â†» Refresh
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <thead>
                <tr
                  style={{
                    color: T.muted,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {['#', 'Agent', 'Total', 'Inbound', 'Outbound', 'Answered', 'Failed', 'Abandoned', 'Duration', 'Avg Handle'].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          padding: '6px 10px',
                          textAlign: h === '#' || h === 'Agent' ? 'left' : 'center',
                          borderBottom: `1px solid ${T.border}`,
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {callStats.map((a: any, i: number) => {
                  const liveItem = allAgents.find(
                    (item: any) =>
                      Number(item?.agent?.id) === Number(a.agent_id),
                  );
                  return (
                    <tr
                      key={a.agent_id}
                      style={{ borderBottom: `1px solid ${T.border}` }}
                    >
                      <td style={{ padding: '8px 10px' }}>{i + 1}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <div
                          onClick={() =>
                            liveItem &&
                            setSelectedAgent({
                              agent: liveItem.agent,
                              user: liveItem.user,
                              dashboard: liveItem.dashboard,
                            })
                          }
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 600,
                            color: liveItem ? T.blue : T.text,
                            cursor: liveItem ? 'pointer' : 'default',
                          }}
                          title={
                            liveItem
                              ? `Click to view ${a.agent_name} details`
                              : a.agent_name
                          }
                        >
                          {liveItem && (
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                flexShrink: 0,
                                backgroundColor:
                                  liveItem.agent?.login_status === '1'
                                    ? T.green
                                    : T.red,
                              }}
                            />
                          )}
                          {a.agent_name}
                        </div>
                      </td>
                      {[
                        { v: a.total_calls, c: T.purple },
                        { v: a.inbound, c: T.cyan },
                        { v: a.outbound, c: T.blue },
                        { v: a.connected, c: T.green },
                        { v: a.failed, c: T.red },
                        { v: a.abandoned, c: T.amber },
                      ].map(({ v, c }: any, idx2: number) => (
                        <td
                          key={idx2}
                          style={{ padding: '8px 10px', textAlign: 'center' }}
                        >
                          <span
                            style={{
                              padding: '2px 10px',
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                              background: `${c}20`,
                              color: c,
                              border: `1px solid ${c}44`,
                            }}
                          >
                            {v}
                          </span>
                        </td>
                      ))}
                      <td
                        style={{
                          padding: '8px 10px',
                          textAlign: 'center',
                          color: T.text,
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {a.total_duration}
                      </td>
                      <td
                        style={{
                          padding: '8px 10px',
                          textAlign: 'center',
                          color: T.text,
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {a.avg_handle}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agent Detail Modal */}
      {selectedAgent &&
        (() => {
          const ag = selectedAgent.agent || {};
          const usr = selectedAgent.user || {};
          const dash = selectedAgent.dashboard || {};
          const profiles = ag?.agentTelephony?.profile || [];
          const agStat =
            callStats.find(
              (s: any) => Number(s.agent_id) === Number(ag.id),
            ) || null;
          const initials = (ag.agent_name || 'A')
            .split(' ')
            .map((w: any) => w[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
          const avatarColor = [
            '#4361ee',
            '#22c55e',
            '#f59e0b',
            '#a855f7',
            '#0ea5e9',
            '#ef4444',
          ][Number(ag.id || 0) % 6];
          const activity = dash?.status
            ? String(dash.status).charAt(0).toUpperCase() +
              String(dash.status).slice(1).toLowerCase().replace(/_/g, ' ')
            : null;

          const total = agStat?.total_calls || 0;
          const connPct = total > 0 ? Math.round((agStat.connected / total) * 100) : 0;
          const failedPct = total > 0 ? Math.round((agStat.failed / total) * 100) : 0;
          const abandonedPct = total > 0 ? Math.round((agStat.abandoned / total) * 100) : 0;
          const inbPct = total > 0 ? Math.round((agStat.inbound / total) * 100) : 0;
          const outPct = total > 0 ? Math.round((agStat.outbound / total) * 100) : 0;

          const gaugeOpts = (value: number, color: string): any => ({
            chart: {
              type: 'radialBar',
              sparkline: { enabled: true },
              background: 'transparent',
              animations: { enabled: true, speed: 600 },
            },
            plotOptions: {
              radialBar: {
                startAngle: -120,
                endAngle: 120,
                hollow: { size: '60%', background: 'transparent' },
                track: {
                  background: T.isDark ? 'rgba(255,255,255,0.07)' : '#e9ecef',
                  strokeWidth: '100%',
                  dropShadow: { enabled: false },
                },
                dataLabels: {
                  show: true,
                  name: { show: false },
                  value: {
                    fontSize: '20px',
                    fontWeight: 900,
                    color,
                    offsetY: 7,
                    formatter: (v: any) => `${Math.round(v)}%`,
                  },
                },
              },
            },
            fill: {
              type: 'gradient',
              gradient: {
                shade: 'dark',
                type: 'horizontal',
                gradientToColors: [color],
                stops: [0, 100],
              },
              colors: [color],
            },
            stroke: { lineCap: 'round', width: 3 },
            grid: { padding: { top: 0, bottom: 0 } },
            theme: { mode: T.isDark ? 'dark' : 'light' },
          });

          const isDark = T.isDark;
          const SIDEBAR_BG = '#0f1535';
          const SIDEBAR_CARD = '#1a2150';

          return (
            <Modal
              show
              onHide={() => setSelectedAgent(null)}
              centered
              dialogClassName="agent-detail-modal"
            >
              <style>{`.agent-detail-modal .modal-dialog { max-width: 420px; } .agent-detail-modal .modal-content { border:none; border-radius:18px; overflow:hidden; }`}</style>
              <Modal.Body
                style={{
                  background: T.isDark ? SIDEBAR_CARD : '#fff',
                  padding: 0,
                  overflow: 'hidden',
                }}
              >
                {/* â”€â”€ Hero banner â”€â”€ */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${SIDEBAR_BG} 0%, ${SIDEBAR_CARD} 60%, #243070 100%)`,
                    padding: '22px 20px 50px',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: -30,
                      right: -30,
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.04)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 20,
                      right: 30,
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.04)',
                    }}
                  />

                  <button
                    onClick={() => setSelectedAgent(null)}
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      color: '#fff',
                      fontSize: 16,
                      cursor: 'pointer',
                      lineHeight: '26px',
                      textAlign: 'center',
                    }}
                  >
                    ×
                  </button>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                    }}
                  >
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: '50%',
                          padding: 3,
                          background: `conic-gradient(${avatarColor}, #4361ee, ${avatarColor})`,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${avatarColor}dd, ${avatarColor}88)`,
                            border: '2px solid #0f1535',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <div
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 13,
                                fontWeight: 900,
                                color: avatarColor,
                              }}
                            >
                              {initials}
                            </div>
                            <div
                              style={{
                                width: 32,
                                height: 14,
                                borderRadius: '50% 50% 0 0',
                                background: 'rgba(255,255,255,0.4)',
                                marginTop: -4,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 2,
                          right: 2,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor:
                            ag.login_status === '1' ? '#22c55e' : '#94a3b8',
                          border: '2px solid #0f1535',
                          boxShadow: `0 0 0 2px ${
                            ag.login_status === '1' ? '#22c55e44' : 'transparent'
                          }`,
                        }}
                      />
                    </div>

                    <div style={{ flex: 1, paddingTop: 2 }}>
                      <div
                        style={{
                          fontSize: 17,
                          fontWeight: 800,
                          color: '#fff',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.2,
                        }}
                      >
                        {ag.agent_name || '-'}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.6)',
                          marginTop: 3,
                        }}
                      >
                        #{ag.agent_number}
                      </div>
                      {usr.email && (
                        <div
                          style={{
                            fontSize: 10,
                            color: 'rgba(255,255,255,0.45)',
                            marginTop: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 200,
                          }}
                        >
                          {usr.email}
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          marginTop: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            padding: '2px 9px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            background: 'rgba(255,255,255,0.12)',
                            color: '#fff',
                          }}
                        >
                          {Number(ag.call_status) === 1
                            ? 'ðŸ“ž In Call'
                            : 'ðŸ’¤ Idle'}
                        </span>
                        {activity && (
                          <span
                            style={{
                              padding: '2px 9px',
                              borderRadius: 999,
                              fontSize: 10,
                              background: 'rgba(255,255,255,0.1)',
                              color: 'rgba(255,255,255,0.85)',
                            }}
                          >
                            {activity}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* â”€â”€ Body â”€â”€ */}
                <div style={{ padding: '0 16px 18px', marginTop: -28 }}>
                  <div style={{ marginBottom: 18 }}>
                    <ProfileBadges profiles={profiles} />
                  </div>

                  {usr?.break_id && (
                    <div
                      style={{
                        marginBottom: 16,
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: `${T.orange}18`,
                        border: `1px solid ${T.orange}44`,
                      }}
                    >
                      <BreakTimer user={usr} currentTime={currentTime} />
                    </div>
                  )}

                  {agStat ? (
                    <>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 10,
                          marginBottom: 12,
                        }}
                      >
                        {[
                          {
                            label: 'Answer Rate',
                            pct: connPct,
                            color: '#22c55e',
                            icon: '✅',
                            sub: `${agStat.connected} of ${total}`,
                          },
                          {
                            label: 'Failed Rate',
                            pct: failedPct,
                            color: '#f59e0b',
                            icon: 'âš ï¸',
                            sub: `${agStat.failed} of ${total}`,
                          },
                        ].map(({ label, pct, color, icon, sub }: any) => (
                          <div
                            key={label}
                            style={{
                              background: isDark
                                ? 'rgba(255,255,255,0.04)'
                                : '#fff',
                              borderRadius: 14,
                              padding: '12px 8px 10px',
                              border: `1.5px solid ${color}33`,
                              textAlign: 'center',
                              boxShadow: `0 2px 10px ${color}15`,
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                                borderRadius: '14px 14px 0 0',
                              }}
                            />

                            <ReactApexChart
                              type="radialBar"
                              height={120}
                              series={[pct]}
                              options={gaugeOpts(pct, color)}
                            />

                            <div style={{ marginTop: -8 }}>
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em',
                                }}
                              >
                                {icon} {label}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: T.muted,
                                  marginTop: 2,
                                }}
                              >
                                {sub}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          background: isDark
                            ? 'rgba(255,255,255,0.04)'
                            : '#f8fafc',
                          borderRadius: 12,
                          padding: '10px 12px',
                          border: `1px solid ${T.border}`,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: T.muted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: 12,
                          }}
                        >
                          Call Distribution
                        </div>
                        {[
                          { label: 'Inbound', pct: inbPct, count: agStat.inbound, color: T.cyan },
                          { label: 'Outbound', pct: outPct, count: agStat.outbound, color: T.blue },
                          { label: 'Answered', pct: connPct, count: agStat.connected, color: T.green },
                          { label: 'Failed', pct: failedPct, count: agStat.failed, color: T.red },
                          { label: 'Abandoned', pct: abandonedPct, count: agStat.abandoned, color: T.amber },
                        ].map(({ label, pct, count, color }: any) => (
                          <div key={label} style={{ marginBottom: 10 }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  color: T.text,
                                  fontWeight: 600,
                                }}
                              >
                                {label}
                              </span>
                              <span
                                style={{
                                  fontSize: 12,
                                  color,
                                  fontWeight: 700,
                                }}
                              >
                                {count}{' '}
                                <span
                                  style={{ color: T.muted, fontWeight: 400 }}
                                >
                                  ({pct}%)
                                </span>
                              </span>
                            </div>
                            <div
                              style={{
                                height: 6,
                                borderRadius: 99,
                                background: isDark
                                  ? 'rgba(255,255,255,0.08)'
                                  : '#e5e7eb',
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  height: '100%',
                                  width: `${pct}%`,
                                  background: color,
                                  borderRadius: 99,
                                  transition: 'width 0.5s ease',
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr',
                          gap: 10,
                        }}
                      >
                        {[
                          {
                            label: 'Total Calls',
                            value: String(agStat.total_calls),
                            color: T.purple,
                            isNum: true,
                          },
                          {
                            label: 'Total Duration',
                            value: agStat.total_duration,
                            color: T.blue,
                            isNum: false,
                          },
                          {
                            label: 'Avg Handle Time',
                            value: agStat.avg_handle,
                            color: T.cyan,
                            isNum: false,
                          },
                        ].map((s: any) => (
                          <div
                            key={s.label}
                            style={{
                              textAlign: 'center',
                              padding: '12px 8px',
                              borderRadius: 12,
                              background: isDark
                                ? 'rgba(255,255,255,0.04)'
                                : '#f8fafc',
                              border: `1px solid ${T.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: s.isNum ? 24 : 16,
                                fontWeight: 900,
                                color: s.color,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {s.value}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: T.muted,
                                marginTop: 3,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                              }}
                            >
                              {s.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '30px 0',
                        color: T.muted,
                        fontSize: 13,
                      }}
                    >
                      No call statistics available for today
                    </div>
                  )}
                </div>
              </Modal.Body>
            </Modal>
          );
        })()}

      {/* Queue Details Modal */}
      <Modal
        show={showQueueModal}
        onHide={() => setShowQueueModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: T.isDark ? '#0f172a' : 'rgba(8,50,105,1)',
            color: '#fff',
            borderBottom: 'none',
          }}
        >
          <Modal.Title style={{ fontSize: 18, fontWeight: 600 }}>
            Call Group Info
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            background: T.isDark ? '#111827' : '#fff',
            color: T.text,
            maxHeight: 480,
            overflowY: 'auto',
            padding: 20,
          }}
        >
          {!queue?.queueData ||
          queue.queueData.filter((q: any) => Object.keys(q.value).length > 0)
            .length === 0 ? (
            <div
              style={{ textAlign: 'center', color: T.muted, padding: 40 }}
            >
              No calls currently waiting in call group
            </div>
          ) : (
            queue.queueData
              .filter((q: any) => Object.keys(q.value).length > 0)
              .map((qItem: any, idx: number) => {
                const queueName = qItem.key.split(':')[3] || qItem.key;
                const calls = Object.values(qItem.value);
                return (
                  <div key={idx} style={{ marginBottom: 24 }}>
                    <h6
                      style={{
                        fontWeight: 700,
                        color: T.isDark ? '#93c5fd' : 'rgba(8,50,105,1)',
                        marginBottom: 10,
                      }}
                    >
                      {queueName}
                    </h6>
                    <div
                      style={{
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: `1px solid ${T.border}`,
                      }}
                    >
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              background: T.isDark ? '#0f172a' : '#f9f9f9',
                            }}
                          >
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 600,
                                color: T.text,
                                textAlign: 'left',
                              }}
                            >
                              Customer Number
                            </th>
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 600,
                                color: T.text,
                                textAlign: 'left',
                              }}
                            >
                              Wait Time
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {calls.map((call: any, cIdx: number) => {
                            const wait = queueWaitSeconds(call.enqueueTime, currentTime) ?? 0;
                            const h = Math.floor(wait / 3600),
                              m = Math.floor((wait % 3600) / 60),
                              s = Math.floor(wait % 60);
                            const waitFmt = `${String(h).padStart(2, '0')}:${String(
                              m,
                            ).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                            return (
                              <tr
                                key={cIdx}
                                style={{
                                  background: T.isDark
                                    ? cIdx % 2 === 0
                                      ? '#111827'
                                      : '#0f172a'
                                    : cIdx % 2 === 0
                                    ? '#f5faff'
                                    : '#fff',
                                  borderTop: `1px solid ${T.border}`,
                                }}
                              >
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    color: T.text,
                                  }}
                                >
                                  {call.calleeTo || '-'}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    color: T.amber,
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {waitFmt}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
          )}
        </Modal.Body>
      </Modal>
      </div>
    </FillPage>
  );
};

export default AgentRealtimeStats;
